import React, { useState, useEffect, useRef } from 'react';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { useNavigate, Link, useParams, useSearchParams } from 'react-router-dom';
import { AuditPhoto } from '@/components/AuditPhoto';
import {
  ArrowLeft,
  ClipboardCheck,
  Building2,
  MapPin,
  User,
  Users,
  Radio,
  Calendar,
  ChevronRight,
  ChevronLeft,
  Save,
  CheckCircle,
  AlertTriangle,
  HelpCircle,
  ExternalLink,
  Loader2,
  Sparkles,
  Award,
  Camera,
  MessageSquare
} from 'lucide-react';
import { api } from '@/utils/api';

interface AuditTemplate {
  id: number;
  name: string;
  description: string;
  program: string;
  elements: any[];
  metadata: any;
}

interface CrewMember {
  id: number;
  name: string;
  email: string;
  role: string;
  employeeId: string;
  certifications: {
    certType: string;
    certName: string;
    expirationDate: string;
    isExpired: boolean;
    daysUntilExpiration: number | null;
  }[];
}

interface User {
  id: number;
  firstName: string;
  lastName: string;
  role: string;
}

const STEPS = [
  { id: 1, name: 'Basic Info', icon: Building2 },
  { id: 2, name: 'Site Details', icon: MapPin },
  { id: 3, name: 'Crew Selection', icon: Users },
  { id: 4, name: 'Audit Checklist', icon: ClipboardCheck },
  { id: 5, name: 'Review & Sign', icon: CheckCircle },
];

const SCOPE_OPTIONS = [
  'Antenna Installation',
  'Antenna and/or Radio Equipment Removal',
  'Coax/Line Installation',
  'Tower Erection',
  'Tower Modification',
  'Maintenance',
  'Civil',
  'Survey/Inspection',
  'Decommission',
  'Other',
];

const TOWER_TYPES = ['Self Support', 'Monopole', 'Guyed', 'Rooftop', 'Water Tank', 'Other'];
const CARRIERS = ['T-Mobile', 'AT&T', 'Verizon', 'Dish', 'US Cellular', 'Other'];

export function CreateAuditPage() {
  // S4 resume/edit: /safety-audits/:id/edit loads an existing audit into the
  // same builder (hydrate formData + responses + template, set draftId).
  const routeParams = useParams<{ id?: string }>();
  const resumeId = routeParams.id && /^[1-9]\d*$/.test(routeParams.id) ? Number(routeParams.id) : null;
  const [editLocked, setEditLocked] = useState(false);
  const [resumeLoadError, setResumeLoadError] = useState('');
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const draftId = useRef<number | null>(null);
  const uploadedFiles = useRef(new Map<File, string>());
  const [photoFiles, setPhotoFiles] = useState<Record<string, File[]>>({});
  const [template, setTemplate] = useState<AuditTemplate | null>(null);
  const [searchParams] = useSearchParams();
  const [templateError, setTemplateError] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [selectedCrew, setSelectedCrew] = useState<CrewMember[]>([]);
  const [showRegulation, setShowRegulation] = useState<string | null>(null);

  // Form data
  const [formData, setFormData] = useState({
    // Basic Info
    program: 'Health and Safety Inspection',
    auditType: 'Site',
    civilOrTower: 'Tower',
    division: 'Services',
    client: '',
    carrierName: '',
    carrierId: '',
    contractorName: '',
    contractorId: '',
    gcSubcontractor: '',
    auditorName: '',
    constructionSupervisor: '',
    startDate: new Date().toISOString().split('T')[0],
    
    // Site Details
    siteId: '',
    siteName: '',
    siteAddress: '',
    city: '',
    state: '',
    zipCode: '',
    latitude: '',
    longitude: '',
    region: '',
    market: '',
    scopeOfWork: '',
    towerType: '',
    
    // Responses
    responses: {} as Record<string, any>,
    
    // Signatures
    auditorSignature: '',
    supervisorSignature: '',
    overallComments: '',
  });

  // S1: warn before unload while audit answers are unsaved
  useUnsavedGuard(Object.keys(formData.responses).length > 0);

  useEffect(() => {
    loadTemplate();
    loadUsers();
  }, []);

  // Prefill the "Basic Information" checklist section from what the auditor
  // already entered on the earlier wizard pages. Values stay fully editable
  // in the checklist (they live in the same responses map).
  useEffect(() => {
    if (step !== 4) return;
    setFormData(prev => {
      const el = prev.responses['element1'];
      if (!el) return prev;
      const prefill: Record<string, any> = {
        'Civil_Tower': prev.civilOrTower,
        'Client': prev.carrierName,
        'Division': prev.division,
        'SOW': prev.scopeOfWork,
        'Tower': prev.towerType,
      };
      const questions = { ...el.questions };
      Object.entries(prefill).forEach(([qid, value]) => {
        // only fill empty answers — never overwrite what the auditor already set
        if (questions[qid] && (questions[qid].response === null || questions[qid].response === '') && value) {
          questions[qid] = { ...questions[qid], response: value };
        }
      });
      return { ...prev, responses: { ...prev.responses, element1: { ...el, questions } } };
    });
  }, [step]);

  const loadTemplate = async () => {
    try {
      const templateId = searchParams.get('templateId');
      if (templateId && !/^[1-9]\d*$/.test(templateId)) throw new Error('Invalid template');
      const response = await api.get(templateId ? `/safety-audits/templates/${templateId}` : '/safety-audits/templates');
      setTemplate(response.data.data);
      
      // Initialize responses structure
      if (response.data.data?.elements) {
        const initialResponses: Record<string, any> = {};
        response.data.data.elements.forEach((element: any) => {
          initialResponses[element.id] = {
            name: element.name,
            applicable: true,
            questions: {},
          };
          element.questions?.forEach((q: any) => {
            initialResponses[element.id].questions[q.id] = {
              response: null,
              applicable: true,
              comment: '',
              findingTitle: '',
              findingDescription: '',
              photos: [],
            };
          });
        });
        setFormData(prev => ({ ...prev, responses: initialResponses }));
      }
    } catch (error) {
      setTemplateError('Unable to load the selected checklist. Return to Audit Templates and try again.');
      console.error('Error loading template:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await api.get('/jha/crew-members');
      setUsers(response.data.data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  // S4 resume: hydrate the builder from an existing audit (edit route).
  useEffect(() => {
    if (resumeId) {
    let cancelled = false;
    (async () => {
      try {
        // External audit MED-3: always reset resume state first so a stale
        // draftId/template/lock from a previous navigation can never survive
        // into the wrong audit.
        draftId.current = null;
        setEditLocked(false);
        setResumeLoadError('');
        setTemplate(null);
        const response = await api.get(`/safety-audits/${resumeId}`);
        const audit = response.data?.data;
        if (audit && !cancelled) {
        if (['COMPLETED', 'CLOSED'].includes(audit.status)) { setEditLocked(true); }
        draftId.current = audit.id;
        const basic = ['program', 'auditType', 'civilOrTower', 'division', 'client', 'carrierName', 'carrierId', 'contractorName', 'contractorId', 'gcSubcontractor', 'auditorName', 'constructionSupervisor'];
        setFormData((prev: any) => ({
          ...prev,
          ...Object.fromEntries(basic.map((k) => [k, (audit as any)[k] ?? prev[k]])),
          startDate: audit.startDate ? String(audit.startDate).split('T')[0] : prev.startDate,
          responses: audit.responses ?? prev.responses,
        }));
        if (audit.templateId) {
          try {
            const t = await api.get(`/safety-audits/templates/${audit.templateId}`);
            if (!cancelled) setTemplate(t.data?.data ?? null);
          } catch { /* template row deleted — builder still hydrates without it */ }
        }
        }
      } catch (error: any) {
        console.error('Error resuming audit:', error);
        if (!cancelled) setResumeLoadError(error?.response?.data?.message || error.message || 'Could not load the audit to edit.');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }
    return undefined;
  }, [resumeId]);

  const loadCrewCertifications = async (userIds: number[]) => {
    if (userIds.length === 0) return;
    try {
      const response = await api.get(`/safety-audits/crew/${userIds.join(',')}`);
      setSelectedCrew(response.data.data);
    } catch (error) {
      console.error('Error loading crew certs:', error);
    }
  };

  const handleCrewToggle = async (userId: number) => {
    const isSelected = selectedCrew.some(c => c.id === userId);
    let newIds: number[];
    
    if (isSelected) {
      newIds = selectedCrew.filter(c => c.id !== userId).map(c => c.id);
      setSelectedCrew(prev => prev.filter(c => c.id !== userId));
    } else {
      newIds = [...selectedCrew.map(c => c.id), userId];
      await loadCrewCertifications(newIds);
    }
  };

  const handleResponseChange = (elementId: string, questionId: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      responses: {
        ...prev.responses,
        [elementId]: {
          ...prev.responses[elementId],
          questions: {
            ...prev.responses[elementId]?.questions,
            [questionId]: {
              ...prev.responses[elementId]?.questions?.[questionId],
              response: value,
            },
          },
        },
      },
    }));
  };

  const handleFindingChange = (elementId: string, questionId: string, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      responses: {
        ...prev.responses,
        [elementId]: {
          ...prev.responses[elementId],
          questions: {
            ...prev.responses[elementId]?.questions,
            [questionId]: {
              ...prev.responses[elementId]?.questions?.[questionId],
              [field]: value,
            },
          },
        },
      },
    }));
  };

  const handleSubmit = async () => {
    if (!template || templateError) {
      alert('Load a valid audit checklist before submitting.');
      return;
    }
    // Validate required fields
    if (!formData.auditorName.trim()) {
      alert('Auditor name is required');
      setSaving(false);
      return;
    }
    // S4 scoring guard (client mirror): an audit with zero answered non-N/A
    // questions cannot be submitted for completion.
    if (editLocked) { alert('This audit is completed or closed — it can no longer be edited.'); setSaving(false); return; }
    const answered = Object.values(formData.responses ?? {}).flatMap((el: any) =>
      el?.questions ? Object.values(el.questions).filter((q: any) => {
        const r = q?.response;
        return r !== undefined && r !== null && r !== '' && r !== 'N/A';
      }) : [],
    );
    if (answered.length === 0) {
      alert('No responses recorded — answer at least one question before completing the audit.');
      setSaving(false);
      return;
    }

    setSaving(true);
    try {
      // Create audit - convert lat/lng to numbers if present
      const submitData = {
        ...formData,
        templateId: template?.id,
        crewMemberIds: selectedCrew.map(c => c.id),
        latitude: formData.latitude ? parseFloat(formData.latitude as any) : undefined,
        longitude: formData.longitude ? parseFloat(formData.longitude as any) : undefined,
      };
      if (!draftId.current) {
        const created = await api.post('/safety-audits', submitData);
        draftId.current = created.data.data.id;
      } else await api.patch(`/safety-audits/${draftId.current}`, submitData);
      const id = draftId.current;
      const responses = JSON.parse(JSON.stringify(formData.responses));
      for (const [key, files] of Object.entries(photoFiles)) {
        const [elementId, questionId] = key.split(':');
        for (const file of files) {
          let url = uploadedFiles.current.get(file);
          if (!url) {
            const data = new FormData(); data.append('file', file);
            const photo = await api.post(`/safety-audits/${id}/photos`, data, { headers: { 'Content-Type': undefined } });
            url = photo.data.data.url as string; uploadedFiles.current.set(file, url);
          }
          const question = responses[elementId].questions[questionId];
          question.photos = [...new Set([...(question.photos || []), url])];
        }
      }

      // Update responses
      await api.put(`/safety-audits/${draftId.current}/responses`, {
        responses,
        status: 'IN_PROGRESS',
      });

      const saved = await api.get(`/safety-audits/${id}`);
      const existingQuestions = new Set((saved.data.data.findings || []).map((f: any) => f.questionId));
      for (const element of template?.elements || []) {
        if (responses[element.id]?.applicable === false) continue;
        for (const question of element.questions || []) {
          const answer = responses[element.id]?.questions?.[question.id];
          if (answer?.response !== 'No' || existingQuestions.has(question.id)) continue;
          await api.post(`/safety-audits/${id}/findings`, { questionId: question.id, elementName: element.name,
            questionText: question.text, title: answer.findingTitle || question.text,
            description: answer.findingDescription || answer.comment || question.text,
            severity: answer.severity || 'MINOR', correctiveAction: answer.correctiveAction || '', photoUrls: answer.photos || [] });
        }
      }
      // Complete if signatures present
      if (formData.auditorSignature) {
        await api.post(`/safety-audits/${draftId.current}/complete`, {
          auditorSignature: formData.auditorSignature,
          supervisorSignature: formData.supervisorSignature,
          overallComments: formData.overallComments,
        });
      }

      navigate(`/safety-audits/${draftId.current}`);
    } catch (error: any) {
      console.error('Error creating audit:', error);
      alert(error.response?.data?.message || 'Failed to create audit');
    } finally {
      setSaving(false);
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Program</label>
                <input aria-label="Program"
                  type="text"
                  value={formData.program}
                  onChange={(e) => setFormData(prev => ({ ...prev, program: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Audit Type</label>
                <select aria-label="Audit Type"
                  value={formData.auditType}
                  onChange={(e) => setFormData(prev => ({ ...prev, auditType: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                >
                  <option>Site</option>
                  <option>Virtual</option>
                  <option>Documentation</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Civil or Tower</label>
                <select aria-label="Civil or Tower"
                  value={formData.civilOrTower}
                  onChange={(e) => setFormData(prev => ({ ...prev, civilOrTower: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                >
                  <option>Tower</option>
                  <option>Civil</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Division</label>
                <input aria-label="Division"
                  type="text"
                  value={formData.division}
                  onChange={(e) => setFormData(prev => ({ ...prev, division: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Carrier/Client</label>
                <select aria-label="Carrier/Client"
                  value={formData.carrierName}
                  onChange={(e) => setFormData(prev => ({ ...prev, carrierName: e.target.value, client: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                >
                  <option value="">Select Carrier...</option>
                  {CARRIERS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Contractor Name</label>
                <input aria-label="Contractor Name"
                  type="text"
                  value={formData.contractorName}
                  onChange={(e) => setFormData(prev => ({ ...prev, contractorName: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  placeholder="If auditing external contractor..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">GC/Subcontractor/Crew Lead</label>
                <input aria-label="GC/Subcontractor/Crew Lead"
                  type="text"
                  value={formData.gcSubcontractor}
                  onChange={(e) => setFormData(prev => ({ ...prev, gcSubcontractor: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Date</label>
                <input aria-label="Date"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Auditor Name</label>
                <input aria-label="Auditor Name"
                  type="text"
                  value={formData.auditorName}
                  onChange={(e) => setFormData(prev => ({ ...prev, auditorName: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Construction Supervisor</label>
                <input aria-label="Construction Supervisor"
                  type="text"
                  value={formData.constructionSupervisor}
                  onChange={(e) => setFormData(prev => ({ ...prev, constructionSupervisor: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Site ID</label>
                <input aria-label="Site ID"
                  type="text"
                  value={formData.siteId}
                  onChange={(e) => setFormData(prev => ({ ...prev, siteId: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  placeholder="e.g., IND-1234"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Site Name</label>
                <input aria-label="Site Name"
                  type="text"
                  value={formData.siteName}
                  onChange={(e) => setFormData(prev => ({ ...prev, siteName: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-300 mb-1">Site Address</label>
                <input aria-label="Site Address"
                  type="text"
                  value={formData.siteAddress}
                  onChange={(e) => setFormData(prev => ({ ...prev, siteAddress: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">City</label>
                <input aria-label="City"
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">State</label>
                <input aria-label="State"
                  type="text"
                  value={formData.state}
                  onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  placeholder="e.g., IN"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Region</label>
                <input aria-label="Region"
                  type="text"
                  value={formData.region}
                  onChange={(e) => setFormData(prev => ({ ...prev, region: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  placeholder="e.g., Midwest"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Market/Office</label>
                <input aria-label="Market/Office"
                  type="text"
                  value={formData.market}
                  onChange={(e) => setFormData(prev => ({ ...prev, market: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  placeholder="e.g., Indianapolis, IN"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Scope of Work</label>
                <select aria-label="Scope of Work"
                  value={formData.scopeOfWork}
                  onChange={(e) => setFormData(prev => ({ ...prev, scopeOfWork: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                >
                  <option value="">Select Scope...</option>
                  {SCOPE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">Tower Type</label>
                <select aria-label="Tower Type"
                  value={formData.towerType}
                  onChange={(e) => setFormData(prev => ({ ...prev, towerType: e.target.value }))}
                  className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                >
                  <option value="">Select Type...</option>
                  {TOWER_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="bg-slate-700/50 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="h-5 w-5 text-amber-400" />
                <span className="font-semibold text-white">Auto-Populate Certifications</span>
              </div>
              <p className="text-sm text-slate-400">
                Select crew members being audited. Their certifications will be automatically loaded from the database for verification.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h3 className="font-semibold text-white mb-3">Available Personnel</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {users.filter(u => !selectedCrew.some(c => c.id === u.id)).map(user => (
                    <button
                      key={user.id}
                      onClick={() => handleCrewToggle(user.id)}
                      className="w-full text-left px-4 py-3 bg-slate-700 rounded-lg hover:bg-slate-600 transition"
                    >
                      <div className="font-medium text-white">{user.firstName} {user.lastName}</div>
                      <div className="text-sm text-slate-400">{user.role}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-white mb-3">Selected Crew ({selectedCrew.length})</h3>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {selectedCrew.map(member => (
                    <div key={member.id} className="bg-emerald-900/30 border border-emerald-700 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-medium text-white">{member.name}</div>
                        <button
                          onClick={() => handleCrewToggle(member.id)}
                          className="text-red-400 hover:text-red-300 text-sm"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="text-sm text-slate-400 mb-2">{member.role}</div>
                      {member.certifications && member.certifications.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-xs text-slate-500 mb-1">Certifications:</p>
                          {member.certifications.map((cert, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <Award className={`h-3 w-3 ${cert.isExpired ? 'text-red-400' : 'text-green-400'}`} />
                              <span className={`text-xs ${cert.isExpired ? 'text-red-400' : 'text-slate-300'}`}>
                                {cert.certName}
                                {cert.expirationDate && (
                                  <span className="text-slate-500 ml-1">
                                    (exp: {new Date(cert.expirationDate).toLocaleDateString()})
                                  </span>
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  {selectedCrew.length === 0 && (
                    <p className="text-slate-500 text-center py-4">No crew selected</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            {template?.elements?.map((element: any) => {
              const elementResponses = formData.responses[element.id];
              const isElementNA = elementResponses?.applicable === false;

              return (
                <div key={element.id} className="bg-slate-700/50 rounded-xl overflow-hidden">
                  {/* Element Header */}
                  <div className="p-4 bg-slate-600/50 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <h3 className="text-lg font-bold text-white">{element.name}</h3>
                      {element.description && (
                        <span className="text-sm text-slate-400">({element.description})</span>
                      )}
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isElementNA}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            responses: {
                              ...prev.responses,
                              [element.id]: {
                                ...prev.responses[element.id],
                                applicable: !e.target.checked,
                              },
                            },
                          }));
                        }}
                        className="w-4 h-4 rounded border-slate-500 bg-slate-700 text-amber-500 focus:ring-amber-500"
                      />
                      <span className={`text-sm font-medium ${isElementNA ? 'text-amber-400' : 'text-slate-400'}`}>
                        N/A Entire Section
                      </span>
                    </label>
                  </div>

                  {/* Questions - collapse if N/A */}
                  {isElementNA ? (
                    <div className="p-6 text-center text-amber-400/70 italic">
                      This section has been marked as Not Applicable
                    </div>
                  ) : (
                    <div className="p-6 space-y-4">
                      {element.questions?.map((question: any) => {
                        const questionData = formData.responses[element.id]?.questions?.[question.id] || {};
                        const response = questionData.response;
                        const isNo = response === 'No' || response === false;
                        const comment = questionData.comment || '';

                        return (
                          <div key={question.id} className="border-b border-slate-600 pb-4 last:border-0">
                            <div className="flex items-start gap-4">
                              <div className="flex-1">
                                <div className="flex items-start gap-2">
                                  <span className="text-xs font-mono text-slate-500 mt-0.5">{question.id}</span>
                                  <p className="text-slate-200">{question.text}</p>
                                  {question.regulation && (
                                    <button
                                      onClick={() => setShowRegulation(showRegulation === question.id ? null : question.id)}
                                      className="text-blue-400 hover:text-blue-300 flex-shrink-0"
                                      title="View regulation"
                                    >
                                      <HelpCircle className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>

                                {/* Regulation Reference */}
                                {showRegulation === question.id && question.regulation && (
                                  <div className="mt-2 p-3 bg-blue-900/30 border border-blue-700 rounded-lg text-sm">
                                    <div className="font-semibold text-blue-300 mb-1">{question.regulation.code}</div>
                                    <div className="text-slate-300">{question.regulation.description}</div>
                                    {question.regulation.link && (
                                      <a
                                        href={question.regulation.link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-2 inline-flex items-center gap-1 text-blue-400 hover:text-blue-300"
                                      >
                                        View Full Standard <ExternalLink className="h-3 w-3" />
                                      </a>
                                    )}
                                  </div>
                                )}
                              </div>

                              {question.type === 'select' && question.options && (
                                <select
                                  aria-label={question.text}
                                  value={response ?? ''}
                                  onChange={(e) => handleResponseChange(element.id, question.id, e.target.value)}
                                  className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                                >
                                  <option value="">Select...</option>
                                  {question.options.map((opt: string) => <option key={opt} value={opt}>{opt}</option>)}
                                </select>
                              )}
                              {question.type === 'text' && (
                                <input
                                  type="text"
                                  aria-label={question.text}
                                  value={response ?? ''}
                                  onChange={(e) => handleResponseChange(element.id, question.id, e.target.value)}
                                  className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm w-56"
                                  placeholder="Enter answer..."
                                />
                              )}
                              {/* S4: numeric + date question types get proper inputs */}
                              {question.type === 'number' && (
                                <input
                                  type="number"
                                  aria-label={question.text}
                                  value={response ?? ''}
                                  onChange={(e) => handleResponseChange(element.id, question.id, e.target.value)}
                                  className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm w-40"
                                  placeholder="Enter number..."
                                  disabled={editLocked || saving}
                                />
                              )}
                              {question.type === 'date' && (
                                <input
                                  type="date"
                                  aria-label={question.text}
                                  value={response ?? ''}
                                  onChange={(e) => handleResponseChange(element.id, question.id, e.target.value)}
                                  className="px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm w-56"
                                  disabled={editLocked || saving}
                                />
                              )}

                              {question.type === 'yesno' && (
                                <div className="flex items-center gap-2">
                                  <button
                                    aria-pressed={response === 'Yes'} aria-label={`Yes: ${question.text}`} onClick={() => handleResponseChange(element.id, question.id, 'Yes')}
                                    className={`px-4 py-2 rounded-lg font-medium transition ${
                                      response === 'Yes'
                                        ? 'bg-green-600 text-white ring-2 ring-green-400'
                                        : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                                    }`}
                                  >
                                    Yes
                                  </button>
                                  <button
                                    aria-pressed={response === 'No'} aria-label={`No: ${question.text}`} onClick={() => handleResponseChange(element.id, question.id, 'No')}
                                    className={`px-4 py-2 rounded-lg font-medium transition ${
                                      response === 'No'
                                        ? 'bg-red-600 text-white ring-2 ring-red-400'
                                        : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                                    }`}
                                  >
                                    No
                                  </button>
                                  <button
                                    aria-pressed={response === 'N/A'} aria-label={`Not applicable: ${question.text}`} onClick={() => handleResponseChange(element.id, question.id, 'N/A')}
                                    className={`px-4 py-2 rounded-lg font-medium transition ${
                                      response === 'N/A'
                                        ? 'bg-amber-500 text-white ring-2 ring-amber-300'
                                        : 'bg-slate-600 text-slate-300 hover:bg-slate-500'
                                    }`}
                                  >
                                    N/A
                                  </button>
                                  {/* Photo Upload Button */}
                                  <button
                                    onClick={() => document.getElementById(`photo-${element.id}-${question.id}`)?.click()}
                                    className="p-2 rounded-lg bg-slate-600 text-slate-300 hover:bg-slate-500 hover:text-white transition"
                                    title="Attach Photo" aria-label={`Attach photo for ${question.id}`}
                                  >
                                    <Camera className="h-5 w-5" />
                                  </button>
                                </div>
                              )}
                            </div>

                            <input id={`photo-${element.id}-${question.id}`} type="file" accept="image/png,image/jpeg" className="hidden" disabled={saving}
                              onChange={e => { const file = e.target.files?.[0]; if (!file) return;
                                if (file.size > 5 * 1024 * 1024) { alert('Photo must be 5 MB or smaller'); return; }
                                const key = `${element.id}:${question.id}`;
                                setPhotoFiles(prev => ({ ...prev, [key]: [...(prev[key] || []), file] })); e.target.value = ''; }} />
                            <div className="flex gap-2 flex-wrap">{(photoFiles[`${element.id}:${question.id}`] || []).map((file, index) => <span key={index} className="text-sm text-slate-300">{file.name}</span>)}
                              {(questionData.photos || []).map((url: string) => <AuditPhoto key={url} url={url} />)}
                            </div>
                            {/* Comment field for all questions */}
                            <div className="mt-3 ml-6">
                              <div className="flex items-center gap-2">
                                <MessageSquare className="h-4 w-4 text-slate-500" />
                                <input
                                  type="text"
                                  aria-label={`Comment for ${question.id}`} value={comment}
                                  onChange={(e) => {
                                    setFormData(prev => ({
                                      ...prev,
                                      responses: {
                                        ...prev.responses,
                                        [element.id]: {
                                          ...prev.responses[element.id],
                                          questions: {
                                            ...prev.responses[element.id]?.questions,
                                            [question.id]: {
                                              ...prev.responses[element.id]?.questions?.[question.id],
                                              comment: e.target.value,
                                            },
                                          },
                                        },
                                      },
                                    }));
                                  }}
                                  className="flex-1 px-3 py-1.5 bg-slate-700/50 border border-slate-600 rounded text-white text-sm placeholder-slate-500"
                                  placeholder="Add comment or note (optional)..."
                                />
                              </div>
                            </div>

                            {/* Finding fields when No */}
                            {isNo && (
                              <div className="mt-4 ml-6 p-4 bg-red-900/20 border border-red-700 rounded-lg space-y-3">
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2 text-red-400 font-medium">
                                    <AlertTriangle className="h-4 w-4" />
                                    Finding Details Required
                                  </div>
                                  <button
                                    onClick={() => document.getElementById(`photo-${element.id}-${question.id}`)?.click()}
                                    className="flex items-center gap-1 px-3 py-1.5 bg-red-800/50 text-red-300 rounded hover:bg-red-700/50 transition text-sm"
                                  >
                                    <Camera className="h-4 w-4" />
                                    Add Photo Evidence
                                  </button>
                                </div>
                                <div>
                                  <label className="block text-sm text-slate-400 mb-1">Finding Title</label>
                                  <input aria-label="Finding Title"
                                    type="text"
                                    value={formData.responses[element.id]?.questions?.[question.id]?.findingTitle || ''}
                                    onChange={(e) => handleFindingChange(element.id, question.id, 'findingTitle', e.target.value)}
                                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                                    placeholder="Brief title for this finding..."
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm text-slate-400 mb-1">Finding Description</label>
                                  <textarea aria-label="Finding Description"
                                    value={formData.responses[element.id]?.questions?.[question.id]?.findingDescription || ''}
                                    onChange={(e) => handleFindingChange(element.id, question.id, 'findingDescription', e.target.value)}
                                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                                    rows={2}
                                    placeholder="Detailed description of the issue..."
                                  />
                                </div>
                                <div className="grid md:grid-cols-2 gap-3">
                                  <div>
                                    <label className="block text-sm text-slate-400 mb-1">Severity</label>
                                    <select aria-label="Severity"
                                      value={formData.responses[element.id]?.questions?.[question.id]?.severity || 'MINOR'}
                                      onChange={(e) => handleFindingChange(element.id, question.id, 'severity', e.target.value)}
                                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                                    >
                                      <option value="OBSERVATION">Observation</option>
                                      <option value="MINOR">Minor</option>
                                      <option value="MAJOR">Major</option>
                                      <option value="CRITICAL">Critical</option>
                                      <option value="STOP_WORK">Stop Work</option>
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-sm text-slate-400 mb-1">Corrective Action</label>
                                    <input aria-label="Corrective Action"
                                      type="text"
                                      value={formData.responses[element.id]?.questions?.[question.id]?.correctiveAction || ''}
                                      onChange={(e) => handleFindingChange(element.id, question.id, 'correctiveAction', e.target.value)}
                                      className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                                      placeholder="Required corrective action..."
                                    />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="bg-slate-700/50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-4">Overall Comments</h3>
              <textarea
                value={formData.overallComments}
                onChange={(e) => setFormData(prev => ({ ...prev, overallComments: e.target.value }))}
                className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white"
                rows={4}
                placeholder="Enter any overall comments or recommendations..."
              />
            </div>

            <div className="bg-slate-700/50 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-4">Signatures</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Auditor Signature</label>
                  <input aria-label="Auditor Signature"
                    type="text"
                    value={formData.auditorSignature}
                    onChange={(e) => setFormData(prev => ({ ...prev, auditorSignature: e.target.value }))}
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white font-signature text-xl"
                    placeholder="Type name as signature..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1">Supervisor Signature</label>
                  <input aria-label="Supervisor Signature"
                    type="text"
                    value={formData.supervisorSignature}
                    onChange={(e) => setFormData(prev => ({ ...prev, supervisorSignature: e.target.value }))}
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white font-signature text-xl"
                    placeholder="Type name as signature..."
                  />
                </div>
              </div>
            </div>

            <div className="bg-emerald-900/30 border border-emerald-700 rounded-xl p-6">
              <div className="flex items-center gap-3">
                <CheckCircle className="h-8 w-8 text-emerald-400" />
                <div>
                  <h3 className="font-bold text-white">Ready to Submit</h3>
                  <p className="text-sm text-slate-400">
                    Review all responses and signatures, then click Submit to complete the audit.
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {templateError && <p role="alert" className="p-4 bg-red-100 text-red-900">{templateError}</p>}
      {resumeLoadError && <p role="alert" className="p-4 bg-red-100 text-red-900">Could not load the audit to edit: {resumeLoadError}. Return to the audit list and try again.</p>}
      {/* Header */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="container mx-auto px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="flex items-center gap-4 min-w-0">
              <Link to="/safety-audits" className="text-slate-400 hover:text-white flex-shrink-0">
                <ArrowLeft className="h-6 w-6" />
              </Link>
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-white truncate">New Safety Audit</h1>
                <p className="text-sm text-slate-400 truncate">{template?.name || 'Health & Safety Inspection'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="bg-slate-800/30 border-b border-slate-700">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {STEPS.map((s, i) => {
              const StepIcon = s.icon;
              const isActive = step === s.id;
              const isComplete = step > s.id;

              return (
                <React.Fragment key={s.id}>
                  <button
                    onClick={() => setStep(s.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                      isActive
                        ? 'bg-emerald-600 text-white'
                        : isComplete
                        ? 'bg-emerald-900/50 text-emerald-400'
                        : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    <StepIcon className="h-5 w-5" />
                    <span className="hidden md:inline">{s.name}</span>
                  </button>
                  {i < STEPS.length - 1 && (
                    <ChevronRight className="h-5 w-5 text-slate-600" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-8 max-w-4xl">
        {renderStepContent()}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-8">
          <button
            onClick={() => setStep(prev => Math.max(1, prev - 1))}
            disabled={step === 1}
            className="px-6 py-3 bg-slate-700 text-white rounded-lg disabled:opacity-50 flex items-center gap-2"
          >
            <ChevronLeft className="h-5 w-5" />
            Previous
          </button>

          {step < 5 ? (
            <button
              onClick={() => setStep(prev => Math.min(5, prev + 1))}
              className="px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 flex items-center gap-2"
            >
              Next
              <ChevronRight className="h-5 w-5" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={saving}
              className="px-8 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg hover:from-emerald-600 hover:to-teal-700 flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-5 w-5" />
                  Submit Audit
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

